// src/pages/Settings/PrivacyData.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;


const generateIdempotencyKey = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

const PrivacyData: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>('faq');

  // API-Driven States
  const [shareUsageData, setShareUsageData] = useState(false);
  const [shareAnalyticsData, setShareAnalyticsData] = useState(false);
  const [allowThirdPartyCookies, setAllowThirdPartyCookies] = useState(false);
  const [emailMarketingOptIn, setEmailMarketingOptIn] = useState(false);
  const [smsMarketingOptIn, setSmsMarketingOptIn] = useState(false);
  const [loading, setLoading] = useState(true);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- API CALL 1: FETCH DATA (GET) ---
  const fetchPrivacySettings = useCallback(async () => {
    setLoading(true);
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      const response = await fetch(`${BASE_URL}/api/settings/user/privacy`, {
        method: 'GET',
        headers: {
          'X-Idempotency-Key': generateIdempotencyKey(),
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch privacy: ${response.status}`);
      const json = await response.json();
      const data = json.data;

      // Map API data to UI state
      setShareUsageData(data.shareUsageData);
      setShareAnalyticsData(data.shareAnalyticsData);
      setAllowThirdPartyCookies(data.allowThirdPartyCookies);
      setEmailMarketingOptIn(data.emailMarketingOptIn);
      setSmsMarketingOptIn(data.smsMarketingOptIn);

    } catch (err: any) {
      toast.error(err.message || 'Failed to load privacy settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrivacySettings();
  }, [fetchPrivacySettings]);

  // --- API CALL 2: SAVE DATA (PUT) ---
  const savePrivacySettings = useCallback(async () => {
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      // React state setter trick to read the ABSOLUTE LATEST live values
      const currentShareUsage = await new Promise<boolean>((resolve) => {
        setShareUsageData((prev) => { resolve(prev); return prev; });
      });
      const currentShareAnalytics = await new Promise<boolean>((resolve) => {
        setShareAnalyticsData((prev) => { resolve(prev); return prev; });
      });
      const currentCookies = await new Promise<boolean>((resolve) => {
        setAllowThirdPartyCookies((prev) => { resolve(prev); return prev; });
      });
      const currentEmail = await new Promise<boolean>((resolve) => {
        setEmailMarketingOptIn((prev) => { resolve(prev); return prev; });
      });
      const currentSms = await new Promise<boolean>((resolve) => {
        setSmsMarketingOptIn((prev) => { resolve(prev); return prev; });
      });

      // EXACT PAYLOAD AS REQUESTED
      const payload = {
        shareUsageData: currentShareUsage,
        shareAnalyticsData: currentShareAnalytics,
        allowThirdPartyCookies: currentCookies,
        emailMarketingOptIn: currentEmail,
        smsMarketingOptIn: currentSms,
        dataRetentionPeriod: "12 months",
        consentType: "GDPR",
        consentGranted: true,
        ipAddress: "192.168.1.100",
        userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64)"
      };

      const response = await fetch(`${BASE_URL}/api/settings/user/privacy`, {
        method: 'PUT',
        headers: {
          'X-Idempotency-Key': generateIdempotencyKey(),
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `Failed to update: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) { /* ignore */ }
        throw new Error(errorMessage);
      }

      toast.success('Privacy settings updated successfully!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update privacy settings');
    }
  }, []);

  // Debounced Save Handler (1 second delay)
  const triggerSave = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      savePrivacySettings();
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Toggles the visibility of a privacy section
  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]'></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className='text-[24px] font-bold text-[#3F5265] mb-2'>
        Privacy and Data
      </h2>
      <p className='text-[#657584] text-[16px] mb-6'>
        We’re committed to protecting your data and giving you control over how
        it’s used. Manage your privacy settings below.
      </p>
      <div className='p-6 bg-white rounded-md border shadow-sm'>

        {/* Share Usage Data */}
        <div className='flex justify-between items-center py-4 border-b border-[#C9D4E5]'>
          <div>
            <p className='text-[#3F5265] font-medium text-[16px]'>Share Usage Data</p>
            <p className='text-[12px] text-[#657584]'>
              Allow us to collect anonymous usage data to improve the product experience.
            </p>
          </div>
          <label className='inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              checked={shareUsageData}
              onChange={() => { setShareUsageData(!shareUsageData); triggerSave(); }}
              className='sr-only'
            />
            <div className={`w-11 h-6 rounded-full transition relative ${shareUsageData ? 'bg-[#00A4BD]' : 'bg-gray-300'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${shareUsageData ? 'translate-x-6' : 'translate-x-1'}`}></div>
            </div>
          </label>
        </div>

        {/* Share Analytics Data */}
        <div className='flex justify-between items-center py-4 border-b border-[#C9D4E5]'>
          <div>
            <p className='text-[#3F5265] font-medium text-[16px]'>Share Analytics Data</p>
            <p className='text-[12px] text-[#657584]'>
              Allow us to share high-level aggregate analytics with trusted partners for benchmarking.
            </p>
          </div>
          <label className='inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              checked={shareAnalyticsData}
              onChange={() => { setShareAnalyticsData(!shareAnalyticsData); triggerSave(); }}
              className='sr-only'
            />
            <div className={`w-11 h-6 rounded-full transition relative ${shareAnalyticsData ? 'bg-[#00A4BD]' : 'bg-gray-300'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${shareAnalyticsData ? 'translate-x-6' : 'translate-x-1'}`}></div>
            </div>
          </label>
        </div>

        {/* Allow Third Party Cookies */}
        <div className='flex justify-between items-center py-4 border-b border-[#C9D4E5]'>
          <div>
            <p className='text-[#3F5265] font-medium text-[16px]'>Allow Third-Party Cookies</p>
            <p className='text-[12px] text-[#657584]'>
              Enable third-party cookies for enhanced marketing and tracking purposes.
            </p>
          </div>
          <label className='inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              checked={allowThirdPartyCookies}
              onChange={() => { setAllowThirdPartyCookies(!allowThirdPartyCookies); triggerSave(); }}
              className='sr-only'
            />
            <div className={`w-11 h-6 rounded-full transition relative ${allowThirdPartyCookies ? 'bg-[#00A4BD]' : 'bg-gray-300'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${allowThirdPartyCookies ? 'translate-x-6' : 'translate-x-1'}`}></div>
            </div>
          </label>
        </div>

        {/* Email Marketing Opt-In */}
        <div className='flex justify-between items-center py-4 border-b border-[#C9D4E5]'>
          <div>
            <p className='text-[#3F5265] font-medium text-[16px]'>Email Marketing Opt-In</p>
            <p className='text-[12px] text-[#657584]'>
              Receive promotional emails, product updates, and newsletter from our team.
            </p>
          </div>
          <label className='inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              checked={emailMarketingOptIn}
              onChange={() => { setEmailMarketingOptIn(!emailMarketingOptIn); triggerSave(); }}
              className='sr-only'
            />
            <div className={`w-11 h-6 rounded-full transition relative ${emailMarketingOptIn ? 'bg-[#00A4BD]' : 'bg-gray-300'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${emailMarketingOptIn ? 'translate-x-6' : 'translate-x-1'}`}></div>
            </div>
          </label>
        </div>

        {/* SMS Marketing Opt-In */}
        <div className='flex justify-between items-center py-4 border-b border-[#C9D4E5]'>
          <div>
            <p className='text-[#3F5265] font-medium text-[16px]'>SMS Marketing Opt-In</p>
            <p className='text-[12px] text-[#657584]'>
              Receive promotional SMS messages and updates on your mobile device.
            </p>
          </div>
          <label className='inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              checked={smsMarketingOptIn}
              onChange={() => { setSmsMarketingOptIn(!smsMarketingOptIn); triggerSave(); }}
              className='sr-only'
            />
            <div className={`w-11 h-6 rounded-full transition relative ${smsMarketingOptIn ? 'bg-[#00A4BD]' : 'bg-gray-300'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${smsMarketingOptIn ? 'translate-x-6' : 'translate-x-1'}`}></div>
            </div>
          </label>
        </div>

        {/* --- FAQ ACCORDION --- */}
        <div className='border border-[#C9D4E5] rounded-md overflow-hidden mt-6'>
          <button
            onClick={() => toggleSection('faq')}
            className={`w-full flex justify-between items-center p-4 text-left font-medium ${openSection === 'faq' ? 'bg-[#E0F8FB]' : 'bg-white'
              }`}
          >
            <div>
              <p className='text-[#3F5265] text-[20px]'>
                Frequently Asked Questions
              </p>
              <p className='text-[#657584] text-[16px] font-normal'>
                Learn more about our Privacy and data policies.
              </p>
            </div>
            {openSection === 'faq' ? (
              <FiChevronUpComponent className='text-[#3F5265]' />
            ) : (
              <FiChevronDownComponent className='text-[#3F5265]' />
            )}
          </button>
          {openSection === 'faq' && (
            <div className='bg-[#E0F8FB] p-4 space-y-4 border-t border-[#C9D4E5] text-[#3F5265] text-[14px]'>
              <div>
                <p className='font-semibold'>
                  Can your team access my account data?
                </p>
                <p className='text-[#657584] text-sm'>
                  Only if you manually enable support access. You’re in full
                  control and can disable it anytime.
                </p>
              </div>
              <div>
                <p className='font-semibold'>
                  Is my personal data shared with third parties?
                </p>
                <p className='text-[#657584] text-sm'>
                  No. We do not share your personal data with third parties
                  without your explicit consent.
                </p>
              </div>
              <div>
                <p className='font-semibold'>
                  How is my data stored and protected?
                </p>
                <p className='text-[#657584] text-sm'>
                  Your data is encrypted both at rest and in transit using
                  industry-standard security practices.
                </p>
              </div>
              <div>
                <p className='font-semibold'>Can I export or delete my data?</p>
                <p className='text-[#657584] text-sm'>
                  Yes, you can export your account data as a ZIP file. If you
                  wish to delete your data, contact support or use the delete
                  option in your account settings.
                </p>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PrivacyData;